import { useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Header from "@/pages/home/components/Header";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { searchContent, type SearchEntry } from "@/mocks/searchIndex";

function typeBadge(type: SearchEntry["type"]) {
  switch (type) {
    case "Page":
      return (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase text-[#0d1f4c] bg-[#0d1f4c]/8 px-2 py-0.5 rounded-full">
          <i className="ri-pages-line text-[10px]"></i>
          Page
        </span>
      );
    case "Resource":
      return (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase text-[#1a8fc1] bg-[#1a8fc1]/10 px-2 py-0.5 rounded-full">
          <i className="ri-file-text-line text-[10px]"></i>
          Resource
        </span>
      );
    case "FAQ":
      return (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
          <i className="ri-question-answer-line text-[10px]"></i>
          FAQ
        </span>
      );
    case "News":
      return (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
          <i className="ri-newspaper-line text-[10px]"></i>
          News
        </span>
      );
  }
}

function SearchResultItem({ entry }: { entry: SearchEntry }) {
  const isHashLink = entry.href.startsWith("/#");
  const hash = isHashLink ? entry.href.substring(2) : undefined;

  const handleHashClick = (e: React.MouseEvent, targetHash: string) => {
    e.preventDefault();
    const id = targetHash;
    if (id) {
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: "smooth" });
        return;
      }
    }
    window.location.href = `/${entry.href.startsWith("/#") ? `#${id}` : entry.href}`;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-100 hover:border-[#1a8fc1]/30 hover:shadow-sm transition-all duration-200 p-5">
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            {typeBadge(entry.type)}
          </div>
          {isHashLink && hash ? (
            <a
              href={`/#${hash}`}
              onClick={(e) => handleHashClick(e, hash)}
              className="block text-base font-semibold text-[#0d1f4c] hover:text-[#1a8fc1] transition-colors mb-1.5 cursor-pointer"
            >
              {entry.title}
            </a>
          ) : (
            <Link
              to={entry.href}
              className="block text-base font-semibold text-[#0d1f4c] hover:text-[#1a8fc1] transition-colors mb-1.5"
            >
              {entry.title}
            </Link>
          )}
          <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">
            {entry.snippet}
          </p>
        </div>
        <div className="flex-shrink-0 pt-1">
          {isHashLink && hash ? (
            <a
              href={`/#${hash}`}
              onClick={(e) => handleHashClick(e, hash)}
              className="w-8 h-8 flex items-center justify-center rounded-md text-[#1a8fc1] hover:bg-[#f4f8fc] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          ) : (
            <Link
              to={entry.href}
              className="w-8 h-8 flex items-center justify-center rounded-md text-[#1a8fc1] hover:bg-[#f4f8fc] transition-colors"
            >
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SearchResults() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const results = useMemo(() => searchContent(query), [query]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Navbar />

      <main className="flex-1">
        {/* Page header */}
        <section className="w-full bg-[#0d1f4c] py-14">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-0.5 bg-[#1a8fc1]"></div>
              <span className="text-[#7ec8e3] text-xs font-semibold tracking-widest uppercase">
                Find Information
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Search Results
            </h1>
            {query ? (
              <p className="text-base text-white/70">
                Search results for: <strong className="text-white">{query}</strong>
              </p>
            ) : (
              <p className="text-base text-white/70 max-w-[560px]">
                Enter a search term to find pages, documents, news, and FAQs.
              </p>
            )}
          </div>
        </section>

        {/* Results area */}
        <section className="w-full bg-white">
          <div className="max-w-[1200px] mx-auto px-6 py-10">
            {query ? (
              <>
                {results.length > 0 ? (
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-5 h-0.5 bg-[#1a8fc1]"></div>
                      <p className="text-sm text-gray-500">
                        {results.length} result{results.length !== 1 ? "s" : ""} found
                      </p>
                    </div>

                    <div className="space-y-4 max-w-[860px]">
                      {results.map((entry, idx) => (
                        <SearchResultItem key={`${entry.title}-${idx}`} entry={entry} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="max-w-[600px]">
                    <div className="bg-[#f8fafc] rounded-lg border border-gray-200 p-8 text-center">
                      <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-[#f4f8fc] mb-4">
                        <i className="ri-search-line text-2xl text-gray-400"></i>
                      </div>
                      <h2 className="text-lg font-semibold text-[#0d1f4c] mb-2">
                        No results found
                      </h2>
                      <p className="text-sm text-gray-500 leading-relaxed max-w-[440px] mx-auto mb-6">
                        No results found. Try searching for a different term or browse the FAQ and Resources pages.
                      </p>
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <Link
                          to="/faq"
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-question-answer-line text-sm"></i>
                          Browse FAQ
                        </Link>
                        <Link
                          to="/resources"
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-folder-line text-sm"></i>
                          Browse Resources
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="max-w-[600px]">
                <div className="bg-[#f8fafc] rounded-lg border border-gray-200 p-8 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-[#f4f8fc] mb-4">
                    <i className="ri-search-line text-2xl text-gray-400"></i>
                  </div>
                  <h2 className="text-base font-semibold text-[#0d1f4c] mb-2">
                    Enter a search term
                  </h2>
                  <p className="text-sm text-gray-500 leading-relaxed max-w-[400px] mx-auto mb-5">
                    Enter a search term to find pages, documents, news, and FAQs.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link
                      to="/faq"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-question-answer-line text-sm"></i>
                      Browse FAQ
                    </Link>
                    <Link
                      to="/resources"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a8fc1] border border-[#1a8fc1]/40 rounded-lg px-4 py-2.5 hover:bg-[#f4f8fc] hover:border-[#1a8fc1] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-folder-line text-sm"></i>
                      Browse Resources
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}